export type CallRecorderState = { recordingId: string; mimeType: string };

function preferredMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "audio/webm";
}

export class CallRecorder {
  private context: AudioContext | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;
  private localSource: MediaStreamAudioSourceNode | null = null;
  private remoteSource: MediaStreamAudioSourceNode | null = null;
  private recorder: MediaRecorder | null = null;
  private state: CallRecorderState | null = null;
  private nextSequence = 0;
  private uploadChain = Promise.resolve();
  private uploadFailure: Error | null = null;
  private stopping = false;

  async start(localStream: MediaStream) {
    if (this.recorder) return;
    this.context = new AudioContext();
    await this.context.resume();
    this.destination = this.context.createMediaStreamDestination();
    this.localSource = this.context.createMediaStreamSource(localStream);
    this.localSource.connect(this.destination);
    const mimeType = preferredMimeType();
    const response = await fetch("/api/recordings", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mimeType }) });
    const created = await response.json().catch(() => ({})) as { id?: string; error?: string };
    if (!response.ok || !created.id) throw new Error(created.error ?? "Recording could not start");
    this.state = { recordingId: created.id, mimeType };
    this.recorder = new MediaRecorder(this.destination.stream, { mimeType });
    this.recorder.addEventListener("dataavailable", (event) => {
      if (!event.data.size) return;
      const sequence = this.nextSequence++;
      this.uploadChain = this.uploadChain.then(() => this.uploadChunk(sequence, event.data)).catch((error) => { this.uploadFailure = error instanceof Error ? error : new Error("Recording upload failed"); });
    });
    this.recorder.start(4_000);
  }

  async attachRemote(stream: MediaStream) {
    if (!this.context || !this.destination) return;
    this.remoteSource?.disconnect();
    this.remoteSource = this.context.createMediaStreamSource(stream);
    this.remoteSource.connect(this.destination);
  }

  private async uploadChunk(sequence: number, blob: Blob) {
    if (!this.state) return;
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetch(`/api/recordings/${encodeURIComponent(this.state.recordingId)}/chunks/${sequence}`, { method: "PUT", headers: { "Content-Type": this.state.mimeType }, body: blob });
        if (response.ok) return;
        const detail = (await response.json().catch(() => ({})) as { error?: string }).error;
        throw new Error(detail ?? "Recording chunk upload failed");
      } catch (error) {
        lastError = error instanceof Error ? error : new Error("Recording chunk upload failed");
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
      }
    }
    throw lastError ?? new Error("Recording chunk upload failed");
  }

  async stop() {
    if (!this.recorder || !this.state) return;
    if (this.stopping) return;
    this.stopping = true;
    await new Promise<void>((resolve) => {
      const recorder = this.recorder!;
      recorder.addEventListener("stop", () => resolve(), { once: true });
      recorder.stop();
    });
    await this.uploadChain;
    if (!this.uploadFailure) {
      const response = await fetch(`/api/recordings/${encodeURIComponent(this.state.recordingId)}/complete`, { method: "POST", keepalive: true });
      if (!response.ok) this.uploadFailure = new Error((await response.json().catch(() => ({})) as { error?: string }).error ?? "Recording finalization failed");
    }
    this.localSource?.disconnect();
    this.remoteSource?.disconnect();
    this.localSource = null;
    this.remoteSource = null;
    this.recorder = null;
    this.destination = null;
    if (this.context && this.context.state !== "closed") await this.context.close();
    this.context = null;
    this.state = null;
  }
}
